import { Box, SxProps } from '@mui/material';
import { Button } from 'system/atoms';
import { FormField } from 'components/FormField';
import { UploadBox, UploadFileItem } from 'components/Upload';
import React, { useState } from 'react';
import { NextButton } from 'components/NextButton';
import { FileWithS3Key } from 'pages/Analyse/NoATSAnalyseCreation';
import { UploadedFileItem } from 'components/Upload/UploadFileItem';

const fileItemsContainer: SxProps = {
  mt: 2
};

const buttonsContainer: SxProps = {
  display: 'flex',
  flexDirection: 'row',
  gap: 2
};

const getFileKey = (file: File) => {
  return `${file.name}-${file.size}-${file.lastModified}`;
};

type Props = {
  filesWithS3Keys: FileWithS3Key[];
  onNext: (filesWithS3Keys: FileWithS3Key[]) => void;
  onSkip?: () => void;
};

export const NoATSUploadCVStep: React.FC<Props> = ({
  filesWithS3Keys: initialFilesWithS3Keys,
  onNext,
  onSkip
}) => {
  const [files, setFiles] = useState<File[]>(
    initialFilesWithS3Keys.map((fileWithS3Key) => fileWithS3Key.file)
  );
  const [cvS3Keys, setCvS3Keys] = useState<string[]>(
    initialFilesWithS3Keys.map((fileWithS3Key) => fileWithS3Key.s3Key)
  );

  const appendS3key = (key: string) => {
    setCvS3Keys((prev) => [...prev, key]);
  };

  const onUpload = async (newFiles: File[]) => {
    setFiles([...files, ...newFiles]);
  };

  const removeFile = (fileIndex: number) => {
    const filesAfterRemoval = files.filter((_, index) => index !== fileIndex);
    const cvS3KeysAfterRemoval = cvS3Keys.filter(
      (_, index) => index !== fileIndex
    );
    setFiles(filesAfterRemoval);
    setCvS3Keys(cvS3KeysAfterRemoval);
  };

  const disableNextButton =
    cvS3Keys.length !== files.length || files.length === 0;

  return (
    <Box>
      <FormField
        width={600}
        title="Review captured data"
        subTitle="Upload CVs and review or download the data collected"
        customComponents={[
          <>
            <UploadBox
              text="PDF, .TXT, .DOCX or .HTML (max 10Mb each)"
              acceptedFiles={[
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/pdf',
                'text/plain',
                'text/html'
              ]}
              onUpload={onUpload}
            />
            <Box sx={fileItemsContainer}>
              {files.map((file, index) => (
                <>
                  {cvS3Keys[index] ? (
                    <UploadedFileItem
                      key={getFileKey(file)}
                      file={file}
                      onDelete={() => removeFile(index)}
                    />
                  ) : (
                    <UploadFileItem
                      key={getFileKey(file)}
                      file={file}
                      onComplete={appendS3key}
                      onDelete={() => removeFile(index)}
                    />
                  )}
                </>
              ))}
            </Box>
          </>,
          <Box sx={buttonsContainer}>
            {onSkip && (
              <Button variant="secondary" onClick={onSkip}>
                Skip
              </Button>
            )}
            <NextButton
              nextText={onSkip ? 'Next' : 'Submit'}
              handleNext={() =>
                onNext(
                  cvS3Keys.map((s3Key, index) => ({
                    file: files[index],
                    s3Key
                  }))
                )
              }
              disabled={disableNextButton}
            />
          </Box>
        ]}
      />
    </Box>
  );
};
